import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import AppHeader from '../components/common/AppHeader';
import LeagueNav from '../components/common/LeagueNav';
import type { League, PlayerPool, PlayerPoolEntry, TennisMatchPerformance } from '../types/database';
import colors from '../theme/colors';
import { flagEmoji } from '../utils/countryFlags';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlayerStats {
  entry: PlayerPoolEntry;
  totalPoints: number;
  matchesPlayed: number;
  wins: number;
  losses: number;
  straightSetWins: number;
  tieBreakWins: number;
  totalAces: number;
  totalDoubleFaults: number;
  avgFirstServePct: number | null;
}

interface Props {
  league: League;
  playerPool: PlayerPool | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function computeStats(entry: PlayerPoolEntry): PlayerStats {
  const perfs: TennisMatchPerformance[] = entry.tennisMatchPerformances ?? [];
  let wins = 0, losses = 0, straightSetWins = 0, tieBreakWins = 0;
  let totalAces = 0, totalDoubleFaults = 0;
  let firstServePctSum = 0, firstServePctCount = 0;

  for (const p of perfs) {
    if (p.won) {
      wins++;
      if (p.straightSets) straightSetWins++;
      if (p.finalSetTieBreak) tieBreakWins++;
    } else {
      losses++;
    }
    if (p.aces != null) totalAces += p.aces;
    if (p.doubleFaults != null) totalDoubleFaults += p.doubleFaults;
    if (p.firstServePct != null) {
      firstServePctSum += p.firstServePct;
      firstServePctCount++;
    }
  }

  return {
    entry,
    totalPoints: perfs.reduce((s, p) => s + p.pointsEarned, 0),
    matchesPlayed: perfs.length,
    wins,
    losses,
    straightSetWins,
    tieBreakWins,
    totalAces,
    totalDoubleFaults,
    avgFirstServePct: firstServePctCount > 0 ? firstServePctSum / firstServePctCount : null,
  };
}

function roundLabel(round: string | undefined): string {
  if (!round) return '';
  const r = round.toLowerCase();
  if (r.includes('final') && r.includes('semi')) return 'SF';
  if (r.includes('quarter')) return 'QF';
  if (r === 'final' || (r.includes('final') && !r.includes('semi') && !r.includes('quarter') && !r.includes('r'))) return 'F';
  if (r.includes('r128')) return 'R128';
  if (r.includes('r64')) return 'R64';
  if (r.includes('r32')) return 'R32';
  if (r.includes('r16')) return 'R16';
  return round.split(' - ').pop() ?? round;
}

const glassCard = (accent = colors.blue.electric) => ({
  background: `linear-gradient(145deg, ${alpha(colors.blue.navy, 0.95)} 0%, ${alpha('#0A1929', 0.98)} 100%)`,
  border: `1px solid ${alpha(accent, 0.2)}`,
  borderRadius: 3,
  position: 'relative' as const,
  overflow: 'hidden' as const,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${alpha(accent, 0.6)}, transparent)`,
  },
});

const menColor = colors.blue.electric;
const womenColor = '#E91E63';

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionDivider: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.07)' }} />
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)' }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.07)' }} />
  </Box>
);

const TOP_N = 10;

const TopScorersSection: React.FC<{ stats: PlayerStats[]; accent: string }> = ({ stats, accent }) => {
  const [showAll, setShowAll] = useState(false);
  const maxPts = stats[0]?.totalPoints ?? 1;
  const visible = showAll ? stats : stats.slice(0, TOP_N);
  const hasMore = stats.length > TOP_N;

  return (
    <Box>
      {visible.map((s, i) => (
        <Box key={s.entry.playerId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.9rem', color: alpha(accent, 0.6), width: 20, textAlign: 'center', flexShrink: 0 }}>
            {i + 1}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)', minWidth: 0, flex: '0 0 120px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {flagEmoji(s.entry.team)} {s.entry.name}
          </Typography>
          {s.entry.seed != null && (
            <Typography sx={{ fontSize: '0.6rem', color: alpha(colors.orange.light, 0.7), flexShrink: 0 }}>
              [{s.entry.seed}]
            </Typography>
          )}
          <Box sx={{ flex: 1, mx: 0.5 }}>
            <Box sx={{ height: 6, borderRadius: 3, bgcolor: alpha(accent, 0.08), overflow: 'hidden' }}>
              <Box sx={{
                height: '100%',
                borderRadius: 3,
                width: `${(s.totalPoints / maxPts) * 100}%`,
                bgcolor: accent,
                boxShadow: `0 0 8px ${alpha(accent, 0.5)}`,
              }} />
            </Box>
          </Box>
          <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.95rem', color: accent, flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
            {s.totalPoints.toFixed(0)}
          </Typography>
        </Box>
      ))}

      {hasMore && (
        <Box
          onClick={() => setShowAll(v => !v)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            mt: 1.5, py: 0.75,
            borderRadius: 1.5,
            border: `1px dashed ${alpha(accent, 0.25)}`,
            cursor: 'pointer',
            color: alpha(accent, 0.7),
            fontSize: '0.72rem',
            fontWeight: 600,
            transition: 'all 0.15s',
            '&:hover': { bgcolor: alpha(accent, 0.05), borderColor: alpha(accent, 0.45), color: accent },
          }}
        >
          {showAll
            ? <><ExpandLessIcon sx={{ fontSize: 14 }} /> Show less</>
            : <><ExpandMoreIcon sx={{ fontSize: 14 }} /> Show all {stats.length} players</>
          }
        </Box>
      )}
    </Box>
  );
};

const BonusBreakdownSection: React.FC<{ stats: PlayerStats[] }> = ({ stats }) => {
  const totals = useMemo(() => {
    let wins = 0, straightSets = 0, tieBreaks = 0, aces = 0, dfs = 0;
    for (const s of stats) {
      wins += s.wins;
      straightSets += s.straightSetWins;
      tieBreaks += s.tieBreakWins;
      aces += s.totalAces;
      dfs += s.totalDoubleFaults;
    }
    return { wins, straightSets, tieBreaks, aces, dfs };
  }, [stats]);

  const items = [
    { label: 'Match Wins', value: totals.wins, color: '#66BB6A' },
    { label: 'Straight Sets', value: totals.straightSets, color: colors.blue.light },
    { label: 'Tie-Break Wins', value: totals.tieBreaks, color: colors.orange.light },
    { label: 'Total Aces', value: totals.aces, color: '#AB47BC' },
    { label: 'Double Faults', value: totals.dfs, color: '#EF5350' },
  ];

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {items.map(({ label, value, color }) => (
        <Box key={label} sx={{
          px: 1.5, py: 1,
          borderRadius: 2,
          bgcolor: alpha(color, 0.08),
          border: `1px solid ${alpha(color, 0.2)}`,
          textAlign: 'center',
          minWidth: 80,
        }}>
          <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.3rem', color, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: '0.6rem', color: alpha(color, 0.7), fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', mt: 0.25 }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const MatchRow: React.FC<{ perf: TennisMatchPerformance }> = ({ perf }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 1,
    px: 1.25, py: 0.75,
    borderRadius: 1.5,
    bgcolor: perf.won ? alpha('#66BB6A', 0.06) : alpha('#EF5350', 0.06),
    border: `1px solid ${perf.won ? alpha('#66BB6A', 0.15) : alpha('#EF5350', 0.12)}`,
    mb: 0.5,
  }}>
    {perf.round && (
      <Chip
        label={roundLabel(perf.round)}
        size="small"
        sx={{
          height: 18,
          fontSize: '0.6rem',
          fontWeight: 700,
          bgcolor: alpha(colors.blue.electric, 0.12),
          color: alpha(colors.blue.light, 0.9),
          border: `1px solid ${alpha(colors.blue.electric, 0.25)}`,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.2 }} noWrap>
        {perf.won ? 'W' : 'L'} vs {perf.opponentName ?? 'Unknown'}
        {perf.opponentSeed != null && (
          <Typography component="span" sx={{ fontSize: '0.65rem', color: alpha(colors.orange.light, 0.7), ml: 0.5 }}>
            [seed {perf.opponentSeed}]
          </Typography>
        )}
      </Typography>
      {perf.setScores.length > 0 && (
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', mt: 0.15 }}>
          {perf.setScores.map(s => `${s.gamesWon}-${s.gamesLost}`).join(', ')}
        </Typography>
      )}
    </Box>
    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
      {perf.straightSets && perf.won && (
        <Chip label="SSW" size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: alpha(colors.blue.light, 0.12), color: colors.blue.light, '& .MuiChip-label': { px: 0.6 } }} />
      )}
      {perf.finalSetTieBreak && perf.won && (
        <Chip label="TB" size="small" sx={{ height: 16, fontSize: '0.55rem', bgcolor: alpha(colors.orange.light, 0.12), color: colors.orange.light, '& .MuiChip-label': { px: 0.6 } }} />
      )}
    </Box>
    <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.88rem', color: perf.won ? '#66BB6A' : '#EF5350', flexShrink: 0 }}>
      {perf.pointsEarned > 0 ? '+' : ''}{perf.pointsEarned.toFixed(0)}
    </Typography>
  </Box>
);

const PlayerCard: React.FC<{ stats: PlayerStats; accent: string }> = ({ stats, accent }) => {
  const [expanded, setExpanded] = useState(false);
  const perfs = stats.entry.tennisMatchPerformances ?? [];
  const sorted = [...perfs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <Box sx={{
      ...glassCard(accent),
      mb: 1.5,
    }}>
      <Box
        onClick={() => setExpanded(e => !e)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: alpha(accent, 0.04) },
        }}
      >
        {/* Seed */}
        {stats.entry.seed != null && (
          <Box sx={{
            width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
            bgcolor: stats.entry.seed <= 8 ? alpha(colors.orange.primary, 0.15) : alpha(colors.text.secondary, 0.08),
            border: `1px solid ${stats.entry.seed <= 8 ? alpha(colors.orange.primary, 0.4) : 'rgba(255,255,255,0.12)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: stats.entry.seed <= 8 ? colors.orange.light : 'rgba(255,255,255,0.4)' }}>
              {stats.entry.seed}
            </Typography>
          </Box>
        )}

        {/* Name + country */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }} noWrap>
            {flagEmoji(stats.entry.team)} {stats.entry.name}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', mt: 0.1 }}>
            {stats.wins}W {stats.losses}L · {stats.matchesPlayed} matches
          </Typography>
        </Box>

        {/* Points */}
        <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.1rem', color: accent, flexShrink: 0 }}>
          {stats.totalPoints.toFixed(0)} pts
        </Typography>

        <IconButton size="small" sx={{ color: 'rgba(255,255,255,0.4)', p: 0.25 }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 1.5 }} />

          {/* Stat grid */}
          {(stats.totalAces > 0 || stats.totalDoubleFaults > 0 || stats.avgFirstServePct != null) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {[
                { label: 'Aces', value: stats.totalAces, color: '#AB47BC' },
                { label: 'DFs', value: stats.totalDoubleFaults, color: '#EF5350' },
                ...(stats.avgFirstServePct != null ? [{ label: '1st Srv%', value: `${stats.avgFirstServePct.toFixed(0)}%`, color: colors.blue.light }] : []),
                { label: 'SS Wins', value: stats.straightSetWins, color: colors.blue.light },
                { label: 'TB Wins', value: stats.tieBreakWins, color: colors.orange.light },
              ].map(item => (
                <Box key={item.label} sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: alpha(item.color as string, 0.08), border: `1px solid ${alpha(item.color as string, 0.2)}`, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1rem', color: item.color, lineHeight: 1 }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.55rem', color: alpha(item.color as string, 0.7), fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', mt: 0.2 }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Match history */}
          {sorted.length > 0 && (
            <>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', mb: 0.75 }}>
                Match History
              </Typography>
              {sorted.map((p, i) => <MatchRow key={p.id ?? i} perf={p} />)}
            </>
          )}

          {sorted.length === 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', py: 1 }}>
              No match records yet
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const TennisAnalyticsPage: React.FC<Props> = ({ league, playerPool }) => {
  const [genderTab, setGenderTab] = useState<'men' | 'women'>('men');
  const [deepDiveSearch, setDeepDiveSearch] = useState('');
  const leagueId = league.id;

  const allStats = useMemo<PlayerStats[]>(() => {
    if (!playerPool) return [];
    return playerPool.players.map(computeStats);
  }, [playerPool]);

  const filteredStats = useMemo(
    () => allStats
      .filter(s => (s.entry.gender ?? s.entry.role) === genderTab)
      .sort((a, b) => b.totalPoints - a.totalPoints),
    [allStats, genderTab],
  );

  const accent = genderTab === 'men' ? menColor : womenColor;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppHeader />
      <LeagueNav
        leagueName={league.name}
        leagueId={leagueId}
        currentPage="Analytics"
      />

      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, pb: { xs: '88px', md: 4 } }}>

        {/* Page header */}
        <Box sx={{
          position: 'relative', pl: 3, py: 2, mb: 3,
          '&::before': {
            content: '""', position: 'absolute',
            left: 0, top: 0, bottom: 0, width: '4px', borderRadius: '4px',
            background: `linear-gradient(180deg, ${accent}, ${alpha(accent, 0.2)})`,
          },
        }}>
          <Typography variant="h4" fontWeight={800} sx={{
            letterSpacing: '-0.02em',
            background: `linear-gradient(90deg, #fff 60%, ${accent})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Tennis Analytics
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.4 }}>
            {league.tournamentName || league.name}
          </Typography>
        </Box>

        {/* Gender tabs */}
        <Tabs
          value={genderTab}
          onChange={(_, v) => { setGenderTab(v); setDeepDiveSearch(''); }}
          sx={{
            mb: 3,
            '& .MuiTab-root': { fontWeight: 700, fontSize: '0.8rem', textTransform: 'none' },
            '& .MuiTabs-indicator': { bgcolor: accent },
          }}
        >
          <Tab label="Men's" value="men" />
          <Tab label="Women's" value="women" />
        </Tabs>

        {!playerPool || filteredStats.length === 0 ? (
          <Card sx={glassCard()}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
                No player data available yet
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Section 1: Top Scorers */}
            <Card sx={{ ...glassCard(accent), mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: alpha(accent, 0.8), mb: 2 }}>
                  Top Fantasy Scorers
                </Typography>
                <TopScorersSection stats={filteredStats} accent={accent} />
              </CardContent>
            </Card>

            {/* Section 2: Bonus Breakdown */}
            <Card sx={{ ...glassCard(accent), mb: 3 }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: alpha(accent, 0.8), mb: 2 }}>
                  Pool-Wide Bonus Breakdown
                </Typography>
                <BonusBreakdownSection stats={filteredStats} />
              </CardContent>
            </Card>

            {/* Section 3: Player Deep Dives */}
            <Box>
              <SectionDivider label="Player Deep Dives" />

              {/* Search */}
              <TextField
                size="small"
                placeholder="Search players..."
                value={deepDiveSearch}
                onChange={e => setDeepDiveSearch(e.target.value)}
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: alpha(accent, 0.35) },
                    '&.Mui-focused fieldset': { borderColor: alpha(accent, 0.6) },
                  },
                  '& .MuiInputBase-input': { color: '#fff', fontSize: '0.85rem' },
                  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />

              {(() => {
                const query = deepDiveSearch.trim().toLowerCase();
                const shown = query
                  ? filteredStats.filter(s => s.entry.name.toLowerCase().includes(query))
                  : filteredStats;
                if (shown.length === 0) {
                  return (
                    <Typography sx={{ textAlign: 'center', py: 3, color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>
                      No players match "{deepDiveSearch}"
                    </Typography>
                  );
                }
                return shown.map(s => (
                  <PlayerCard key={s.entry.playerId} stats={s} accent={accent} />
                ));
              })()}
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default TennisAnalyticsPage;
